import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useGame } from '../../contexts/GameContext';
import levelsData from '../../game/levels.json';

// ── Colour palette ────────────────────────────────────────────────────────────
const COLORS = {
  perfect: '#4caf50',
  good:    '#FF9600',
  missed:  '#f44336',
};
const PIE_COLORS = [COLORS.perfect, COLORS.good, COLORS.missed];

// ── Stars row component (used in Stars Per Level) ─────────────────────────────
const StarsRow = ({ stars }) =>
  [1, 2, 3, 4, 5].map((s) =>
    s <= stars ? (
      <StarIcon key={s} sx={{ fontSize: 16, color: '#ffd700' }} />
    ) : (
      <StarBorderIcon key={s} sx={{ fontSize: 16, color: '#d0d0d0' }} />
    )
  );

// ── Score colour helper ───────────────────────────────────────────────────────
const scoreColor = (s) => (s >= 85 ? '#4caf50' : s >= 60 ? '#FF9600' : '#f44336');

// ── Custom tooltip for score trend ───────────────────────────────────────────
const ScoreTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.97)', boxShadow: 3 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {payload[0].value}%
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {payload[0].payload.date}
      </Typography>
    </Paper>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const PerformanceCharts = () => {
  const { getAllHistory, progress } = useGame();
  const allHistory = getAllHistory();

  // Score trend data (last 20 attempts across all levels)
  const scoreTrendData = (() => {
    const all = [];
    Object.entries(allHistory).forEach(([, attempts]) => {
      attempts.forEach((a) =>
        all.push({ ...a, date: new Date(a.timestamp).toLocaleDateString() })
      );
    });
    all.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    return all.slice(-20).map((a, i) => ({ attempt: i + 1, score: a.score, date: a.date }));
  })();

  // Stars per level — only levels the user has seen (unlocked)
  const starsData = levelsData.levels.map((level) => ({
    id: level.id,
    name: level.name,
    stars: progress.levelStars?.[level.id] || 0,
    score: progress.highScores?.[level.id] || 0,
    unlocked: (progress.unlockedLevels || [1]).includes(level.id) ||
              (progress.completedLevels || []).includes(level.id),
  }));
  // Only show levels that have been touched (attempted or unlocked)
  const playedLevels = starsData.filter((l) => l.score > 0);

  // Note accuracy breakdown
  const accuracyData = (() => {
    let perfect = 0, good = 0, missed = 0;
    Object.values(allHistory).forEach((attempts) => {
      attempts.forEach((a) => {
        if (a.noteAccuracy) {
          perfect += a.noteAccuracy.perfect || 0;
          good    += a.noteAccuracy.good    || 0;
          missed  += a.noteAccuracy.missed  || 0;
        }
      });
    });
    const total = perfect + good + missed;
    if (total === 0) return [];
    return [
      { name: 'Perfect', value: perfect, pct: Math.round((perfect / total) * 100) },
      { name: 'Good',    value: good,    pct: Math.round((good    / total) * 100) },
      { name: 'Missed',  value: missed,  pct: Math.round((missed  / total) * 100) },
    ];
  })();

  const hasAnyData = scoreTrendData.length > 0 || accuracyData.length > 0;

  if (!hasAnyData) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">📊 No data yet</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Complete some levels to see your performance charts!
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* ── 1. Score Trend ─────────────────────────────────────────────── */}
      {scoreTrendData.length > 0 && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom>📈 Score Trend</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Last {scoreTrendData.length} attempts across all levels
          </Typography>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={scoreTrendData} margin={{ top: 5, right: 16, bottom: 20, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="attempt"
                label={{ value: 'Attempt', position: 'insideBottom', offset: -10, fontSize: 12 }}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `${v}%`}
                width={40}
              />
              <Tooltip content={<ScoreTooltip />} />
              <Line
                type="monotone"
                dataKey="score"
                stroke="#58CC02"
                strokeWidth={3}
                dot={{ fill: '#58CC02', r: 4, strokeWidth: 0 }}
                activeDot={{ r: 7, strokeWidth: 0 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      <Grid container spacing={3}>
        {/* ── 2. Stars Per Level ────────────────────────────────────────── */}
        <Grid size={{ xs: 12, md: accuracyData.length > 0 ? 6 : 12 }}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
            <Typography variant="h6" gutterBottom>⭐ Stars Per Level</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Your best star rating for each level played
            </Typography>

            {playedLevels.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Play a level to see your stars here
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {playedLevels.map((level) => (
                  <Box key={level.id}>
                    {/* Level name row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: '#58CC02',
                            bgcolor: '#e8f5e9',
                            borderRadius: 1,
                            px: 0.8,
                            py: 0.2,
                            flexShrink: 0,
                            fontSize: '0.7rem',
                          }}
                        >
                          L{level.id}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {level.name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: 1 }}>
                        <StarsRow stars={level.stars} />
                      </Box>
                    </Box>
                    {/* Score bar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          flex: 1,
                          height: 8,
                          bgcolor: '#f0f0f0',
                          borderRadius: 4,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${level.score}%`,
                            height: '100%',
                            bgcolor: scoreColor(level.score),
                            borderRadius: 4,
                            transition: 'width 0.6s ease',
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: scoreColor(level.score), minWidth: 32, textAlign: 'right' }}
                      >
                        {level.score}%
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ── 3. Note Accuracy Donut ────────────────────────────────────── */}
        {accuracyData.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Typography variant="h6" gutterBottom>🎯 Note Accuracy</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Breakdown across all your attempts
              </Typography>

              {/* Donut chart */}
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={accuracyData}
                    cx="50%"
                    cy="50%"
                    innerRadius="52%"
                    outerRadius="72%"
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                    paddingAngle={3}
                    labelLine={false}
                  >
                    {accuracyData.map((_, i) => (
                      <Cell
                        key={i}
                        fill={PIE_COLORS[i]}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [`${props.payload.pct}%  (${value} notes)`, name]} />
                  {/* SVG centre label */}
                  <text
                    x="50%" y="43%"
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={26}
                    fontWeight={800}
                    fill={COLORS.perfect}
                  >
                    {accuracyData[0]?.pct}%
                  </text>
                  <text
                    x="50%" y="57%"
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={12}
                    fill="#888"
                  >
                    Perfect
                  </text>
                </PieChart>
              </ResponsiveContainer>

              {/* Stat legend */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 1 }}>
                {accuracyData.map((item, i) => (
                  <Box
                    key={item.name}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      bgcolor: `${PIE_COLORS[i]}18`,
                      border: `1.5px solid ${PIE_COLORS[i]}55`,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      minWidth: 72,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 800, color: PIE_COLORS[i], lineHeight: 1 }}>
                      {item.pct}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3 }}>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: PIE_COLORS[i], fontWeight: 600 }}>
                      {item.value} notes
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PerformanceCharts;
